import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import { Button, Text } from '@ui';
import { Tooltip } from '@ui/atoms/Tooltip';
import { Toolbar } from '@ui/molecules/Toolbar';
import { Popup } from '@ui/molecules/Popup';
import ReviewIssuesModal from '@features/IIMReview/components/ReviewIssuesModal';
import ConfirmBulkActionModal from '@features/IIMReview/components/ConfirmBulkActionModal';
import IssueDetailBlade from '@features/IIMReview/components/IssueDetailBlade';
import { IssueStatusEnum, ModeIA } from '@features/IIMReview/types';
import { IIM_BUCKET_FILTER_EVENT } from '@features/IIMReview/events';
import useVisibilityListener from '../../../ActivityFeed/hooks/useVisibilityListener';
import useIIMIssuesGrid from '../../hooks/useIIMIssuesGrid';
import { useBulkUpdateIssues, useBulkMarkIssuesForSuggestion } from '../../hooks/useUpdateIssue';
import { useBulkUndoIssues } from '../../hooks/useUndoStatusIssue';
import TrialRunLoader from '../TrialRunLoader';
import IssuesGridFilterBar from './IssuesGridFilterBar';
import LegacyIssuesGridFilterBar from './LegacyIssuesGridFilterBar';
import IssuesGridTable from './IssuesGridTable';
import { EMPTY_FILTERS } from './types';
import { isNonSuggestableIssueType, NON_SUGGESTABLE_ISSUE_TYPES } from '../../utils/nonSuggestableIssueTypes';
import './styles.scss';
const NS = 'features.iim_review.issues_grid';
const BULK_NS = `${NS}.bulk_actions`;
const BUCKET_FILTER_TOAST_DURATION_MS = 5000;
const PAGE_SIZE = 25;
// Statuses that can be acted on (suggest/dismiss) — resolved is excluded; it requires undo first
const ACTIONABLE_STATUSES = [IssueStatusEnum.Detected, IssueStatusEnum.Skipped, IssueStatusEnum.DismissedByTech];
export default function IssuesGrid({ inspectionId, modeIA, isRunning, ipaScorecardEnabled = false }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [groupBy, setGroupBy] = useState('none');
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [selectedIssueId, setSelectedIssueId] = useState(null);
    const [isBladeOpen, setIsBladeOpen] = useState(false);
    const [isGroupByOpen, setIsGroupByOpen] = useState(false);
    const groupByButtonRef = useRef(null);
    const isModeReadOnly = modeIA === ModeIA.ReadOnly || modeIA === ModeIA.Trial || modeIA === ModeIA.Teaser;
    const activeBucketKeyRef = useRef(null);
    useEffect(() => {
        const handler = () => setIsReviewModalOpen(true);
        document.addEventListener('openSendForCorrection', handler);
        return () => document.removeEventListener('openSendForCorrection', handler);
    }, []);
    useEffect(() => {
        // TEMP(tmp_hide_new_ai_summary): bucket filtering is IP-15683 only; remove guard when it ships.
        if (!ipaScorecardEnabled)
            return undefined;
        const handler = (event) => {
            const { issueTypes, bucketKey, count, totalCount: allCount } = event.detail;
            const isToggleOff = activeBucketKeyRef.current === bucketKey;
            activeBucketKeyRef.current = isToggleOff ? null : bucketKey;
            setFilters((prev) => (Object.assign(Object.assign({}, prev), { issueTypes: isToggleOff ? [] : issueTypes })));
            setSelectedItems([]);
            setSelectAllAcrossPages(false);
            toast.success(translate('bucket_filter_toast', { namespace: NS, count: isToggleOff ? allCount : count }), {
                duration: BUCKET_FILTER_TOAST_DURATION_MS,
                ariaProps: { role: 'status', 'aria-live': 'polite' }
            });
        };
        document.addEventListener(IIM_BUCKET_FILTER_EVENT, handler);
        return () => document.removeEventListener(IIM_BUCKET_FILTER_EVENT, handler);
    }, [ipaScorecardEnabled]);
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('openSendForCorrection') === 'true') {
            setIsReviewModalOpen(true);
        }
    }, []);
    useVisibilityListener(containerRef, () => {
        setIsVisible(true);
    });
    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);
    // Reset to page 1 when filters, search, or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, debouncedSearch, sortBy, sortDirection, groupBy]);
    const activeFilters = Object.assign(Object.assign({}, filters), { search: debouncedSearch });
    const { data, isFetching: isLoading, error } = useIIMIssuesGrid(inspectionId, activeFilters, sortBy, sortDirection, groupBy, currentPage, PAGE_SIZE, modeIA, {
        enabled: isVisible
    });
    // Derive selectedIssue from live query data so it stays fresh after mutations
    const selectedIssue = useMemo(() => {
        var _a;
        if (!selectedIssueId || !(data === null || data === void 0 ? void 0 : data.issues))
            return null;
        return (_a = data.issues.find((i) => i.id === selectedIssueId)) !== null && _a !== void 0 ? _a : null;
    }, [selectedIssueId, data === null || data === void 0 ? void 0 : data.issues]);
    // --- Bulk action hooks ---
    // For individual selection: pass selectedItems as issueIds
    // For "select all across pages": pass empty array — the service will use inspectionId + filterStatuses
    const effectiveIssueIds = selectAllAcrossPages ? [] : selectedItems;
    const bulkMarkMutation = useBulkMarkIssuesForSuggestion({
        inspectionId,
        issueIds: effectiveIssueIds
    });
    const bulkUpdateMutation = useBulkUpdateIssues({
        inspectionId,
        issueIds: effectiveIssueIds
    });
    const bulkUndoMutation = useBulkUndoIssues({
        inspectionId,
        issueIds: effectiveIssueIds
    });
    const selectedCount = selectAllAcrossPages ? (_b = (_a = data === null || data === void 0 ? void 0 : data.pagination) === null || _a === void 0 ? void 0 : _a.totalCount) !== null && _b !== void 0 ? _b : 0 : selectedItems.length;
    const totalCount = (_d = (_c = data === null || data === void 0 ? void 0 : data.pagination) === null || _c === void 0 ? void 0 : _c.totalCount) !== null && _d !== void 0 ? _d : 0;
    const isMutating = bulkMarkMutation.isPending || bulkUpdateMutation.isPending || bulkUndoMutation.isPending;
    const noItemsSelected = selectedCount === 0;
    // Determine if any selected issues are undoable and which type
    const { hasUndoableSelected, undoActionType } = useMemo(() => {
        if (noItemsSelected || !(data === null || data === void 0 ? void 0 : data.issues))
            return { hasUndoableSelected: false, undoActionType: 'undo_dismiss' };
        const selectedIssues = data.issues.filter((issue) => selectedItems.includes(issue.id));
        const hasSuggested = selectedIssues.some((issue) => issue.markedForSuggestion);
        const hasDismissed = selectedIssues.some((issue) => issue.status === IssueStatusEnum.DismissedByBackOffice);
        const hasResolved = selectedIssues.some((issue) => issue.status === IssueStatusEnum.Resolved);
        const hasUndoable = hasSuggested || hasDismissed || hasResolved;
        // Priority: suggested > dismissed > resolved
        const actionType = hasSuggested
            ? 'undo_suggest'
            : hasDismissed
                ? 'undo_dismiss'
                : 'undo_resolve';
        return { hasUndoableSelected: hasUndoable, undoActionType: actionType };
    }, [noItemsSelected, data === null || data === void 0 ? void 0 : data.issues, selectedItems]);
    // Determine if "Suggest Correction" should be disabled due to non-suggestable issue types
    const { suggestDisabledByType, suggestTooltip, hasNonSuggestableWarning } = useMemo(() => {
        if (noItemsSelected || !(data === null || data === void 0 ? void 0 : data.issues)) {
            return { suggestDisabledByType: false, suggestTooltip: '', hasNonSuggestableWarning: false };
        }
        const tooltip = translate('suggest_disabled_tooltip', { namespace: BULK_NS });
        if (selectAllAcrossPages) {
            // When user has an explicit issue type filter, use it as source of truth.
            // Otherwise derive from current page issues (already filtered by status/search/etc).
            const typesInScope = filters.issueTypes.length > 0 ? filters.issueTypes : [...new Set(data.issues.map((i) => i.issueType))];
            const nonSuggestableInSet = typesInScope.filter((t) => NON_SUGGESTABLE_ISSUE_TYPES.includes(t));
            const allAreNonSuggestable = nonSuggestableInSet.length > 0 && nonSuggestableInSet.length === typesInScope.length;
            if (allAreNonSuggestable) {
                return { suggestDisabledByType: true, suggestTooltip: tooltip, hasNonSuggestableWarning: false };
            }
            return {
                suggestDisabledByType: false,
                suggestTooltip: '',
                hasNonSuggestableWarning: nonSuggestableInSet.length > 0
            };
        }
        // Individual / current-page selection
        const selectedIssues = data.issues.filter((issue) => selectedItems.includes(issue.id));
        const hasNonSuggestable = selectedIssues.some((issue) => isNonSuggestableIssueType(issue.issueType));
        if (hasNonSuggestable) {
            return { suggestDisabledByType: true, suggestTooltip: tooltip, hasNonSuggestableWarning: false };
        }
        return { suggestDisabledByType: false, suggestTooltip: '', hasNonSuggestableWarning: false };
    }, [noItemsSelected, data === null || data === void 0 ? void 0 : data.issues, filters.issueTypes, selectedItems, selectAllAcrossPages]);
    // Determine if actions should be disabled due to resolved issues in the selection
    const { hasResolvedSelected, resolvedTooltip } = useMemo(() => {
        var _a, _b;
        if (noItemsSelected || !(data === null || data === void 0 ? void 0 : data.issues)) {
            return { hasResolvedSelected: false, resolvedTooltip: '' };
        }
        const tooltip = translate('resolved_disabled_tooltip', { namespace: BULK_NS });
        if (selectAllAcrossPages) {
            const statusesInScope = filters.statuses.length > 0
                ? filters.statuses
                : (_b = (_a = data.filterOptions) === null || _a === void 0 ? void 0 : _a.statuses) !== null && _b !== void 0 ? _b : [...new Set(data.issues.map((i) => i.status))];
            const hasResolved = statusesInScope.includes(IssueStatusEnum.Resolved);
            return { hasResolvedSelected: hasResolved, resolvedTooltip: hasResolved ? tooltip : '' };
        }
        const selectedIssues = data.issues.filter((issue) => selectedItems.includes(issue.id));
        const hasResolved = selectedIssues.some((issue) => issue.status === IssueStatusEnum.Resolved);
        return { hasResolvedSelected: hasResolved, resolvedTooltip: hasResolved ? tooltip : '' };
    }, [
        noItemsSelected,
        data === null || data === void 0 ? void 0 : data.issues,
        (_e = data === null || data === void 0 ? void 0 : data.filterOptions) === null || _e === void 0 ? void 0 : _e.statuses,
        filters.statuses,
        selectedItems,
        selectAllAcrossPages
    ]);
    const suggestDisabled = suggestDisabledByType || hasResolvedSelected;
    const suggestDisabledTooltip = hasResolvedSelected ? resolvedTooltip : suggestTooltip;
    const groupByOptions = useMemo(() => [
        { value: 'none', label: translate('group_by_none', { namespace: NS }) },
        { value: 'issueType', label: translate('group_by_issue_type', { namespace: NS }) },
        { value: 'status', label: translate('group_by_status', { namespace: NS }) },
        { value: 'inspectableType', label: translate('group_by_device_type', { namespace: NS }) },
        { value: 'impact', label: translate('group_by_impact', { namespace: NS }) }
    ], []);
    const groupByLabel = useMemo(() => {
        const selected = groupByOptions.find((opt) => opt.value === groupBy);
        if (!selected || groupBy === 'none')
            return translate('group_by', { namespace: NS });
        return `${translate('group_by', { namespace: NS })}: ${selected.label}`;
    }, [groupBy, groupByOptions]);
    const handleFiltersChange = useCallback((newFilters) => {
        activeBucketKeyRef.current = null;
        setFilters(newFilters);
    }, []);
    // --- Selection handlers ---
    const handleSort = useCallback((dataKey, direction) => {
        if (direction === null) {
            setSortBy(null);
            setSortDirection('asc');
        }
        else {
            const columnMap = {
                issueType: 'issue_type',
                status: 'status',
                inspectable: 'inspectable_type',
                impact: 'impact'
            };
            setSortBy(columnMap[dataKey] || dataKey);
            setSortDirection(direction);
        }
    }, []);
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        setSelectedItems([]);
        setSelectAllAcrossPages(false);
    }, []);
    const handleSelectionChange = useCallback((itemId, checked) => {
        setSelectAllAcrossPages(false);
        setSelectedItems((prev) => (checked ? [...prev, itemId] : prev.filter((id) => id !== itemId)));
    }, []);
    const handleSelectAll = useCallback((selectAll) => {
        setSelectAllAcrossPages(false);
        if (selectAll && (data === null || data === void 0 ? void 0 : data.issues)) {
            setSelectedItems(data.issues.map((issue) => issue.id));
        }
        else {
            setSelectedItems([]);
        }
    }, [data === null || data === void 0 ? void 0 : data.issues]);
    const handleSelectAllAcrossPages = useCallback(() => {
        setSelectAllAcrossPages(true);
        // Also select all on current page visually
        if (data === null || data === void 0 ? void 0 : data.issues) {
            setSelectedItems(data.issues.map((issue) => issue.id));
        }
    }, [data === null || data === void 0 ? void 0 : data.issues]);
    const handleGroupSelectAll = useCallback((groupIssueIds, selectAll) => {
        setSelectAllAcrossPages(false);
        if (selectAll) {
            setSelectedItems((prev) => [...new Set([...prev, ...groupIssueIds])]);
        }
        else {
            setSelectedItems((prev) => prev.filter((id) => !groupIssueIds.includes(id)));
        }
    }, []);
    const handleRowClick = useCallback((issue) => {
        setSelectedIssueId(issue.id);
        setIsBladeOpen(true);
    }, []);
    const handleBladeClose = useCallback(() => {
        setIsBladeOpen(false);
        setSelectedIssueId(null);
    }, []);
    const handleBladeNavigate = useCallback((issue) => {
        setSelectedIssueId(issue.id);
    }, []);
    const handleSearchChange = useCallback((value) => {
        setSearchInput(value);
    }, []);
    // --- Bulk action handlers ---
    const clearSelection = useCallback(() => {
        setSelectedItems([]);
        setSelectAllAcrossPages(false);
    }, []);
    const openConfirmModal = useCallback((actionType) => {
        setPendingAction(actionType);
        setIsConfirmModalOpen(true);
    }, []);
    const closeConfirmModal = useCallback(() => {
        setIsConfirmModalOpen(false);
        setPendingAction(null);
    }, []);
    const handleConfirmAction = useCallback(() => {
        if (!pendingAction)
            return;
        closeConfirmModal();
        const onSuccess = (messageKey) => {
            toast.success(translate(messageKey, { namespace: BULK_NS, count: selectedCount }));
            clearSelection();
        };
        const onError = (messageKey) => {
            toast.error(translate(messageKey, { namespace: BULK_NS }));
        };
        switch (pendingAction) {
            case 'suggest':
                bulkMarkMutation.mutate({ marked: true }, {
                    onSuccess: () => onSuccess('suggest_success'),
                    onError: () => onError('suggest_error')
                });
                break;
            case 'dismiss':
                bulkUpdateMutation.mutate({ status: IssueStatusEnum.DismissedByBackOffice }, {
                    onSuccess: () => onSuccess('dismiss_success'),
                    onError: () => onError('dismiss_error')
                });
                break;
            case 'undo_dismiss':
            case 'undo_suggest':
            case 'undo_resolve':
                bulkUndoMutation.mutate(undefined, {
                    onSuccess: () => onSuccess('undo_success'),
                    onError: () => onError('undo_error')
                });
                break;
        }
    }, [
        pendingAction,
        closeConfirmModal,
        selectedCount,
        clearSelection,
        bulkMarkMutation,
        bulkUpdateMutation,
        bulkUndoMutation
    ]);
    // --- Render ---
    if (error) {
        return (React.createElement("div", { className: "issues-grid__error", ref: containerRef },
            React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
            React.createElement("span", null, translate('error', { namespace: 'features.iim_review.issues_list' }))));
    }
    if ((data === null || data === void 0 ? void 0 : data.runStatus) === 'in_progress' && modeIA === ModeIA.Trial) {
        return (React.createElement("div", { className: "issues-grid", ref: containerRef },
            React.createElement(TrialRunLoader, null)));
    }
    return (React.createElement("div", { className: "issues-grid", ref: containerRef },
        React.createElement("div", { className: "issues-grid__content" },
            ipaScorecardEnabled ? (React.createElement(IssuesGridFilterBar, { filters: filters, onFiltersChange: handleFiltersChange, filterOptions: data === null || data === void 0 ? void 0 : data.filterOptions, searchValue: searchInput, onSearchChange: handleSearchChange, onResetSelection: clearSelection })) : (React.createElement(LegacyIssuesGridFilterBar, { filters: filters, onFiltersChange: setFilters, filterOptions: data === null || data === void 0 ? void 0 : data.filterOptions, searchValue: searchInput, onSearchChange: handleSearchChange, onResetSelection: clearSelection })),
            React.createElement(Toolbar, null,
                selectedCount > 0 && !isModeReadOnly && (React.createElement(Toolbar.Selection, { count: selectedCount, totalCount: totalCount, selectAll: selectAllAcrossPages, onSelectAll: handleSelectAllAcrossPages, selectAllLabel: translate('select_all', { namespace: BULK_NS, count: totalCount }) })),
                !isModeReadOnly && (React.createElement(Toolbar.Group, null,
                    suggestDisabled ? (React.createElement(Tooltip, { content: React.createElement("span", { style: { whiteSpace: 'normal' } }, suggestDisabledTooltip), position: "top" },
                        React.createElement(Button, { disabled: true },
                            React.createElement("i", { className: "fa-light fa-message-lines" }),
                            ' ',
                            translate('suggest_correction', { namespace: BULK_NS })))) : (React.createElement(Button, { onClick: () => openConfirmModal('suggest'), disabled: noItemsSelected || isMutating },
                        React.createElement("i", { className: "fa-light fa-message-lines" }),
                        " ",
                        translate('suggest_correction', { namespace: BULK_NS }))),
                    hasResolvedSelected ? (React.createElement(Tooltip, { content: React.createElement("span", { style: { whiteSpace: 'normal' } }, resolvedTooltip), position: "top" },
                        React.createElement(Button, { disabled: true },
                            React.createElement("i", { className: "fa-light fa-xmark" }),
                            " ",
                            translate('dismiss', { namespace: BULK_NS })))) : (React.createElement(Button, { onClick: () => openConfirmModal('dismiss'), disabled: noItemsSelected || isMutating },
                        React.createElement("i", { className: "fa-light fa-xmark" }),
                        " ",
                        translate('dismiss', { namespace: BULK_NS }))),
                    React.createElement(Button, { onClick: () => openConfirmModal(undoActionType), disabled: !hasUndoableSelected || isMutating },
                        React.createElement("i", { className: "fa-light fa-arrow-rotate-left" }),
                        " ",
                        translate('undo', { namespace: BULK_NS })))),
                React.createElement(Toolbar.Group, null,
                    React.createElement(Button, { ref: groupByButtonRef, isMenu: true, onClick: () => setIsGroupByOpen((prev) => !prev) },
                        React.createElement("i", { className: "fa-light fa-grid-2-plus" }),
                        " ",
                        groupByLabel),
                    React.createElement(Popup, { anchor: groupByButtonRef.current, show: isGroupByOpen, onClose: () => setIsGroupByOpen(false), position: "bottom-left" },
                        React.createElement(Popup.Section, null,
                            React.createElement(Popup.ActionList, { padded: true }, groupByOptions.map((option) => (React.createElement(Button, { key: option.value, type: "button", className: `issues-grid__group-by-option${option.value === groupBy ? ' issues-grid__group-by-option--selected' : ''}`, onClick: () => {
                                    setGroupBy(option.value);
                                    setIsGroupByOpen(false);
                                } }, option.label))))))),
                isRunning && (React.createElement(Toolbar.Group, { className: "issues-grid__run-in-progress-group" },
                    React.createElement(Text, { variant: "x-small", className: "issues-grid__run-in-progress" },
                        React.createElement("i", { className: "fa-light fa-spinner-third fa-spin issues-grid__run-in-progress-icon" }),
                        ' ',
                        translate('run_in_progress', { namespace: NS }))))),
            React.createElement("div", { className: "issues-grid__table-area" },
                isLoading && (React.createElement("div", { className: "issues-grid__loading" },
                    React.createElement("i", { className: "fa-light fa-spinner-third fa-spin" }),
                    React.createElement("span", null, translate('loading', { namespace: 'features.iim_review.issues_list' })))),
                !isLoading && (data === null || data === void 0 ? void 0 : data.issues) && data.issues.length > 0 && (React.createElement(IssuesGridTable, { issues: data.issues, groupBy: groupBy, sortBy: sortBy, sortDirection: sortDirection, onSort: handleSort, selectedItems: selectedItems, onSelectionChange: handleSelectionChange, onSelectAll: handleSelectAll, onRowClick: handleRowClick, activeItemId: selectedIssueId, onGroupSelectAll: handleGroupSelectAll, onPageChange: handlePageChange, serverCurrentPage: (_f = data.pagination) === null || _f === void 0 ? void 0 : _f.currentPage, serverTotalPages: (_g = data.pagination) === null || _g === void 0 ? void 0 : _g.totalPages, serverTotalItems: (_h = data.pagination) === null || _h === void 0 ? void 0 : _h.totalCount, pageSize: PAGE_SIZE })),
                !isLoading && ((_j = data === null || data === void 0 ? void 0 : data.issues) === null || _j === void 0 ? void 0 : _j.length) === 0 && (React.createElement("div", { className: "issues-grid__empty" },
                    React.createElement("div", { className: "issues-grid__empty-content" },
                        React.createElement("i", { className: "fa-solid fa-party-horn fa-6x" }),
                        React.createElement("span", null, translate('title', { namespace: `${NS}.empty` }))))))),
        React.createElement(ReviewIssuesModal, { isOpen: isReviewModalOpen, onClose: () => setIsReviewModalOpen(false), inspectionId: inspectionId, modeIA: modeIA }),
        React.createElement(ConfirmBulkActionModal, { isOpen: isConfirmModalOpen, onClose: closeConfirmModal, onConfirm: handleConfirmAction, actionType: pendingAction || 'dismiss', count: selectedCount, hasNonSuggestableWarning: hasNonSuggestableWarning }),
        React.createElement(IssueDetailBlade, { issue: selectedIssue, issues: (data === null || data === void 0 ? void 0 : data.issues) || [], isOpen: isBladeOpen, onClose: handleBladeClose, onNavigateToIssue: handleBladeNavigate, inspectionId: inspectionId, modeIA: modeIA })));
}
